import * as React from "react";
import { cn } from "@hireart/component-library";
import PropTypes from "prop-types";
import { Drawer as DrawerPrimitive } from "vaul";

const Drawer = ({ shouldScaleBackground = true, ...props }) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    shouldDrag={(node) => {
      // Fix vaul's bug where shouldDrag tries to access tagName of null
      if (!node || !node.tagName) {
        return false;
      }
      // Use vaul's default shouldDrag logic for valid elements
      const shouldNotDrag = node.closest(
        'button, [role="button"], input, textarea, select, a[href]'
      );
      return !shouldNotDrag;
    }}
    // Try disabling drag entirely as a test
    // dismissible={false}
    {...props}
  />
);
Drawer.displayName = "Drawer";
Drawer.propTypes = {
  shouldScaleBackground: PropTypes.bool,
};
const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerPortal = DrawerPrimitive.Portal;
const DrawerClose = DrawerPrimitive.Close;
const DrawerOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50", className)}
    style={{
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      ...props.style,
    }}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;
DrawerOverlay.propTypes = {
  className: PropTypes.string,
};
const DrawerContent = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          "bg-background fixed inset-x-0 bottom-0 z-50 flex h-auto flex-col rounded-t-[10px] border",
          className
        )}
        style={{
          // Ensure drawer doesn't extend past viewport on mobile
          maxHeight:
            "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
          top: "env(safe-area-inset-top)",
          bottom: "env(safe-area-inset-bottom)",
          marginTop: "1.5rem",
          // Reduced from mt-24 to mt-6
          // Add mobile-specific styles to prevent textarea focus issues
          WebkitOverflowScrolling: "touch",
          // Enable smooth scrolling on iOS
          touchAction: "pan-y", // Allow vertical scrolling but prevent horizontal panning
        }}
        {...props}
      >
        {/* Stop contextmenu from reaching DrawerPrimitive.Content. Vaul's onContextMenu
            calls handleOnPointerUp with a stale pointer event whose target can be null,
            causing a crash in shouldDrag. Stopping propagation here prevents that. */}
        <div
          className="flex flex-1 flex-col overflow-hidden"
          onContextMenu={(e) => e.stopPropagation()}
        >
          <div className="bg-muted mx-auto mt-4 h-2 w-[100px] rounded-full" />
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
);
DrawerContent.displayName = "DrawerContent";
DrawerContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};
const DrawerHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "gap-1.5 drawer-header-safe grid p-4 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";
DrawerHeader.propTypes = {
  className: PropTypes.string,
};
const DrawerFooter = ({ className, ...props }) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";
DrawerFooter.propTypes = {
  className: PropTypes.string,
};
const DrawerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;
DrawerTitle.propTypes = {
  className: PropTypes.string,
};
const DrawerDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("body-2 text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;
DrawerDescription.propTypes = {
  className: PropTypes.string,
};
export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
